import { rdbV1FiltersYearsList } from '@on3/api';
import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { DraftPage, type IDraftPageProps } from 'components/Db/Draft/DraftPage';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

interface IDraft extends IDraftPageProps, IGenericPageProps {}

const NFLDraft = ({ draftList, years, ...props }: IDraft) => {
  const router = useRouter();
  const year = router.query.year as string;
  const round = (router.query.round as string) || '1';
  const description = `A listing of round ${round} NFL Draft picks for the year ${year}.`;
  const title = `${year} NFL Draft Picks - Round ${round}`;
  const draftUrl = `https://www.on3.com/nfl/draft/${year}/`;

  return (
    <PageWrapper
      {...props}
      canonical={round === '1' ? draftUrl : `${draftUrl}?round=${round}`}
      description={description}
      title={title}
    >
      <DatabaseLayout>
        <DraftPage draftList={draftList} years={years} />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { year, round } = ctx.query;

  const params = {
    sportKey: 1,
    round: round || 1,
    year,
  };

  try {
    const [draftList, years] = await Promise.allSettled([
      proxyApi.get(`/public/rdb/v1/drafts`, { params }).then((r) => r.data),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      draftList: draftList.status === 'fulfilled' ? draftList.value : [],
      years: years.status === 'fulfilled' ? years.value : [],
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(NFLDraft);
